import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header, PullToRefresh, MediaThumb } from '@ah/design/mobile';
import { Video } from 'lucide-react';
import {
  VIDEO_CHANNEL_DETAIL_QUERY,
  type ChannelListItem,
  type VideoListItem,
} from '../shared/index.ts';
import { VideoRow, VideoListSkeleton, ErrorRetry } from './_parts.tsx';

type ChannelDetailData = ChannelListItem & {
  latestVideos: VideoListItem[];
};

export function ChannelDetail() {
  const gql = useGql();
  const { id = '' } = useParams<{ id: string }>();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['videos', 'channel', id],
    queryFn: async () => {
      const res = await gql.request<{ videoChannelById: ChannelDetailData | null }>(
        VIDEO_CHANNEL_DETAIL_QUERY,
        { id },
      );
      return res.videoChannelById;
    },
    enabled: !!id,
  });

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title={data?.title ?? 'Channel'} backTo="/videos/channels" />
      <PullToRefresh className="flex-1 px-3 py-3" onRefresh={() => refetch()}>
        {isLoading ? (
          <VideoListSkeleton />
        ) : error || !data ? (
          <ErrorRetry label="Could not load channel." onRetry={() => refetch()} />
        ) : (
          <div className="space-y-4">
            <header className="flex gap-3 items-center pb-2">
              <MediaThumb
                src={data.imageUrl}
                className="w-16 h-16 rounded-full shrink-0"
                iconSize={20}
              />
              <div className="min-w-0 flex-1">
                <p className="font-semibold leading-tight truncate">{data.title}</p>
                {data.handle ? (
                  <p className="text-xs text-muted-foreground truncate">{data.handle}</p>
                ) : null}
                {data.subscriberCount || data.videoCount ? (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {data.subscriberCount ? `${formatBigNumber(data.subscriberCount)} subscribers` : null}
                    {data.subscriberCount && data.videoCount ? ' · ' : ''}
                    {data.videoCount ? `${data.videoCount} videos` : null}
                  </p>
                ) : null}
              </div>
            </header>

            {data.latestVideos.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Video size={28} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No videos imported yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {data.latestVideos.map((v) => <VideoRow key={v.id} v={v} />)}
              </div>
            )}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}

function formatBigNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}
